import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { prop } from "ramda"
import type { StateObject } from "../../../redux/types"
import { getRecordEditId } from "../../../router/selectors"
import type { ModifierGroupsTableDataItem } from "./ModifierGroupsTable"
import { isValidModifierGroupFormItemDisplayType, type ModifierGroupFormItem } from "./ModifierGroupForm"
import type { AutoCompleteSelectionItem } from "../components/AssignItemAutoComplete"
import { getSelectedLocationOverrideId } from "../../../common/locationsPicker/selectors"
import type {
    CategoryModifier,
    CategoryModifierGroup,
    CategoryModifierGroupResponse,
    CategoryModifierResponse,
    CategoryOptionGroup,
    CategoryOptionGroupResponse,
    ModifierGroupItemOverride,
} from "../../../../graphql/types"
import { computeOverriddenValue, applyFiltersToTableData, formatModifiersPriceRange } from "../utils"
import { findById } from "../../../../utils/functional"
import {
    getItemsCategoriesSearchValue,
    getModifierGroupsData,
    getModifiersData,
    getOptionGroupsData,
} from "../selectors"
import { ItemsAndCategoriesTableFilters } from "../types"

export const getModifierGroupById = (state: StateObject, id: Maybe<string>): Maybe<CategoryModifierGroup> => {
    const modifierGroups: Maybe<CategoryModifierGroupResponse> = getModifierGroupsData(state)

    if (!modifierGroups || !id) {
        return null
    }

    const group: Maybe<CategoryModifierGroup> = findById(id, modifierGroups.data)
    return group || null
}

export const getDataForModifierGroupsTable = (
    state: StateObject,
    filters: ItemsAndCategoriesTableFilters
): ModifierGroupsTableDataItem[] => {
    const selectedLocationId = getSelectedLocationOverrideId(state)
    const modifierGroups: Maybe<CategoryModifierGroupResponse> = getModifierGroupsData(state)
    const searchValue = getItemsCategoriesSearchValue(state)

    if (!modifierGroups) {
        return []
    }

    const data = modifierGroups.data.map(group => {
        const isActiveOverriddenValue = computeOverriddenValue(
            modifierGroups.overrides,
            selectedLocationId,
            group.id,
            prop("isActive")
        )
        const priceOverriddenValues = computeOverriddenValue(
            modifierGroups.overrides,
            selectedLocationId,
            group.id,
            prop("price")
        )
        const priceFromModifiers = group.modifiers.map(m => m.price || 0)
        return {
            id: group.id,
            name: group.name,
            exclusion: group.isExclusion,
            modifiers: group.modifiers.length,
            price: {
                value:
                    selectedLocationId && priceOverriddenValues != null
                        ? // @ts-expect-error
                          formatModifiersPriceRange([...priceFromModifiers, ...(priceOverriddenValues || [])])
                        : formatModifiersPriceRange(priceFromModifiers),
                override:
                    priceOverriddenValues != null &&
                    JSON.stringify(priceOverriddenValues) !== JSON.stringify(group.price),
            },
            isActive: isActiveOverriddenValue != null ? isActiveOverriddenValue : group.isActive,
            isActiveOverriden: isActiveOverriddenValue != null && isActiveOverriddenValue !== group.isActive,
            isSelectable: true,
        }
    })
    // @ts-expect-error
    return applyFiltersToTableData(data, searchValue, filters)
}

export const getEditGroupId = (state: StateObject) => getRecordEditId(state)

const buildModifierGroupFormItem = (state: StateObject, group: CategoryModifierGroup): ModifierGroupFormItem => ({
    modifierGroupId: group.id,
    name: group.name,
    displayName: group.displayName,
    isActive: group.isActive,
    isActivePOS: group.isActivePOS,
    isActiveMIB: group.isActiveMIB,
    isActiveKiosk: group.isActiveKiosk,
    display: isValidModifierGroupFormItemDisplayType(group.displayType) ? group.displayType : "scale",
    exclusion: group.isExclusion,
    isMinimumSelection: !!group.min,
    minimumSelection: group.min ? group.min : 0,
    isMaximumSelection: !!group.max,
    maximumSelection: group.max ? group.max : 0,
    isPricedAsGroup: group.groupPrice != null,
    modifiersIncludedCount: group.free ? group.free : 0,
    modifiersIncludedForPrice: group.groupPrice ? group.groupPrice / 100 : 0,
    items: group.modifiers.map(m => ({
        groupItemId: m.id,
        modifierId: m.categoryId,
        name: m.name,
        price: m.price ? m.price / 100 : 0,
        groupExtra: m.supplement ? m.supplement / 100 : 0,
        isActive: getModifierIsActive(state, m.categoryId, null),
    })),
    addedToItems: group.addedToItems,
})

export const getModifierGroupFormData = (state: StateObject): Maybe<ModifierGroupFormItem> => {
    const modifierGroups: Maybe<CategoryModifierGroupResponse> = getModifierGroupsData(state)
    const editGroupId = getEditGroupId(state)

    if (!modifierGroups || !editGroupId) {
        return null
    }

    const group: Maybe<CategoryModifierGroup> = findById(editGroupId, modifierGroups.data)

    if (!group) {
        return null
    }

    return buildModifierGroupFormItem(state, group)
}

export const getModifierGroupFormItemById = (state: StateObject, id: Maybe<string>): Maybe<ModifierGroupFormItem> => {
    const group = getModifierGroupById(state, id)

    if (!group) {
        return null
    }

    return buildModifierGroupFormItem(state, group)
}

export const getModifierIsActive = (state: StateObject, id: string, selectedLocationId: Maybe<string>): boolean => {
    const modifiers: Maybe<CategoryModifierResponse> = getModifiersData(state)
    const modifier: Maybe<CategoryModifier> = findById(id, modifiers ? modifiers.data : [])

    if (!modifier || !modifiers) {
        return false
    }

    if (!selectedLocationId) {
        return modifier ? modifier.isActive : false
    }

    const overriddenValue = computeOverriddenValue(
        modifiers.overrides,
        selectedLocationId,
        modifier.id,
        prop("isActive")
    )
    // @ts-expect-error
    return overriddenValue != null ? overriddenValue : modifier.isActive
}

export const getAllModifierGroupsNew = (state: StateObject): CategoryModifierGroup[] => {
    const modifierGroups: Maybe<CategoryModifierGroupResponse> = getModifierGroupsData(state)
    return modifierGroups ? modifierGroups.data : []
}

export const getAllOptionGroupsNew = (state: StateObject): CategoryOptionGroup[] => {
    const optionGroups: Maybe<CategoryOptionGroupResponse> = getOptionGroupsData(state)
    return optionGroups ? optionGroups.data : []
}

export const getModifierGroupFormDataLocation = (state: StateObject): Maybe<ModifierGroupFormItem> => {
    const modifierGroups: Maybe<CategoryModifierGroupResponse> = getModifierGroupsData(state)
    const selectedLocationId = getSelectedLocationOverrideId(state)
    const editGroupId = getEditGroupId(state)

    if (!modifierGroups || !editGroupId) {
        return null
    }

    const group: Maybe<CategoryModifierGroup> = findById(editGroupId, modifierGroups.data)

    if (!group) {
        return null
    }

    const overriddenActive = computeOverriddenValue(
        modifierGroups.overrides,
        selectedLocationId,
        group.id,
        prop("isActive")
    )
    const overriddenMin = computeOverriddenValue(modifierGroups.overrides, selectedLocationId, group.id, prop("min"))
    const overriddenMax = computeOverriddenValue(modifierGroups.overrides, selectedLocationId, group.id, prop("max"))
    const overridenGroupPrice = computeOverriddenValue(
        modifierGroups.overrides,
        selectedLocationId,
        group.id,
        prop("groupPrice")
    )
    // @ts-expect-error
    const modifiersOverriden: Maybe<ModifierGroupItemOverride[]> = computeOverriddenValue(
        modifierGroups.overrides,
        selectedLocationId,
        group.id,
        prop("modifiers")
    )
    return {
        modifierGroupId: group.id,
        name: group.name,
        displayName: group.displayName,
        // @ts-expect-error
        isActive: overriddenActive != null ? overriddenActive : group.isActive,
        isActiveOverriden: overriddenActive != null && overriddenActive !== group.isActive,
        isActivePOS: group.isActivePOS,
        isActiveMIB: group.isActiveMIB,
        isActiveKiosk: group.isActiveKiosk,
        display: isValidModifierGroupFormItemDisplayType(group.displayType) ? group.displayType : "scale",
        exclusion: group.isExclusion,
        isMinimumSelection: overriddenMin != null ? !!overriddenMin : !!group.min,
        // @ts-expect-error
        minimumSelection: overriddenMin != null ? overriddenMin : group.min,
        minimumSelectionOverriden: overriddenMin != null && overriddenMin !== group.min,
        isMaximumSelection: overriddenMax != null ? !!overriddenMax : !!group.max,
        // @ts-expect-error
        maximumSelection: overriddenMax != null ? overriddenMax : group.max,
        maximumSelectionOverriden: overriddenMax != null && overriddenMax !== group.max,
        isPricedAsGroup: overridenGroupPrice != null ? !!overridenGroupPrice : !!group.groupPrice,
        modifiersIncludedCount: group.free ? group.free : 0,
        modifiersIncludedForPrice: group.groupPrice ? group.groupPrice / 100 : 0,
        items: group.modifiers.map(m => {
            const override = modifiersOverriden && modifiersOverriden.find(k => k.id === m.id)
            return {
                groupItemId: m.id,
                modifierId: m.categoryId,
                name: m.name,
                price: override && override.price != null ? override.price / 100 : m.price ? m.price / 100 : 0,
                priceOverriden: !!(override && override.price != null),
                groupExtra:
                    override && override.supplement != null
                        ? override.supplement / 100
                        : m.supplement
                          ? m.supplement / 100
                          : 0,
                groupExtraOverriden: !!(override && override.supplement != null),
                isActive: getModifierIsActive(state, m.categoryId, selectedLocationId),
            }
        }),
        addedToItems: group.addedToItems,
    }
}

export const getModificationsAutocompleteData = (state: StateObject): AutoCompleteSelectionItem[] => {
    const modifiersData = getModifiersData(state)

    if (modifiersData && modifiersData.data) {
        return modifiersData.data.map(m => ({
            id: m.id,
            title: m.name,
        }))
    }

    return []
}
